import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { TestDetailsWord, VerbtrainingSelectedWord } from "p6m-p6u";
import React, { useMemo } from "react";
import DesignConstants from "../../../constants/DesignConstants";
import { createExerciseKey } from "../../../helpers/PDFHelper";

/**spreadsheet-style label so exercises with more than 26 items keep counting: a, b, ... z, aa, ab, ...*/
const _itemLabel = (index: number) => {
    let label = "";
    for (let i = index; i >= 0; i = Math.floor(i / 26) - 1) {
        label = String.fromCharCode(97 + (i % 26)) + label;
    }
    return label;
};
/**remove quotation marks "\""*/
const _sanitize = (sentence: string) => {
    return sentence.replaceAll('"', "");
};

interface PDFGapSentencesProps {
    selectedWords: Array<TestDetailsWord | VerbtrainingSelectedWord>;
    isSolution?: boolean;
}

const PDFGapSentences: React.FC<PDFGapSentencesProps> = (props) => {
    const gapSentencesKey = useMemo(() => {
        return createExerciseKey(props.selectedWords);
    }, [props.selectedWords]); // to avoid duplicates when switching between A and B

    const renderAlphabetChar = (i: number) => <Text style={styles.text}>{`${_itemLabel(i)})  `}</Text>;

    const renderGapSentence = (sentence: string | undefined, i: number) => {
        return (
            sentence && (
                <View
                    key={`${gapSentencesKey}_${_itemLabel(i)}_${i}`}
                    style={styles.sentence}
                >
                    {renderAlphabetChar(i)}
                    <Text style={styles.text}>{_sanitize(sentence)} </Text>
                </View>
            )
        );
    };

    const renderSolutionSentence = (selectedWord: TestDetailsWord, i: number) => {
        const splitGapSentence = selectedWord.gap_sentence?.split(/_+/) ?? [];
        const solutionSentence = (
            <View
                key={`${gapSentencesKey}_${_itemLabel(i)}_${i}`}
                style={styles.sentence}
            >
                {renderAlphabetChar(i)}
                <Text style={styles.text}>{_sanitize(splitGapSentence[0])}</Text>
                <Text style={[styles.text, styles.bold]}>{selectedWord.answer}</Text>
                {_sanitize(splitGapSentence[1]).trim().substring(0, 1) === "-" && <Text style={styles.text}> </Text>}
                <Text style={styles.text}>{_sanitize(splitGapSentence[1])}</Text>
            </View>
        );
        return solutionSentence;
    };

    const renderSentences = () => {
        return props.selectedWords.map((word, i) => {
            return (
                "id" in word &&
                word.id &&
                (props.isSolution ? renderSolutionSentence(word, i) : renderGapSentence(word.gap_sentence, i))
            );
        });
    };

    return <View style={styles.container}>{renderSentences()}</View>;
};

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: DesignConstants.PDF.GAP_S,
        display: "flex",
        flexDirection: "column",
        gap: DesignConstants.PDF.GAP_XS,
    },
    sentence: {
        flexDirection: "row",
        paddingTop: DesignConstants.PDF.GAP_XXS,
    },
    text: {
        fontSize: DesignConstants.PDF.FONTSIZE_REGULAR,
    },
    bold: {
        fontWeight: DesignConstants.PDF.SEMIBOLD,
    },
});

export default PDFGapSentences;
